export const REQUEST_STATUSES = ['in_progress', 'completed', 'cancelled'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const BD_REVIEW_STATUSES = ['not_sent', 'pending_review', 'returned', 'approved'] as const;
export type BdReviewStatus = (typeof BD_REVIEW_STATUSES)[number];

/** BD reviews the compiled dossier in BDS (SC-NBD); MExP only sends it and records the decision. */
export const BD_REVIEW_STATUS_LABELS: Record<BdReviewStatus, string> = {
  not_sent: 'Not sent to BD',
  pending_review: 'With BD (BDS)',
  returned: 'Returned by BD',
  approved: 'Approved by BD',
};

/** Where a request was raised: in BDS by SC-NBD, in MExP by RA/MIS, or by an exporter in the client portal. */
export const REQUEST_ORIGINS = ['bds', 'mexp', 'client'] as const;

/** What a client asks for in a dossier requisition. */
export const CLIENT_REQUEST_TYPES = ['dossier', 'guidelines', 'documents'] as const;
export type ClientRequestType = (typeof CLIENT_REQUEST_TYPES)[number];

export const CLIENT_REQUEST_TYPE_LABELS: Record<ClientRequestType, string> = {
  dossier: 'Dossier checklist',
  guidelines: 'Guidelines',
  documents: 'Specific documents',
};

/** Requisition types the client must send at least one reference file with (their checklist, format or earlier certificate). */
export const REFERENCE_REQUIRED_TYPES: readonly ClientRequestType[] = ['dossier', 'guidelines'];

/** Worklists on the staff Document requests list; each is a tile on the staff dashboard. */
export const REQUEST_FLAGS = ['open_queries', 'awaiting_client', 'to_verify', 'returned', 'overdue', 'date_proposals'] as const;
export type RequestFlag = (typeof REQUEST_FLAGS)[number];

export const REQUEST_FLAG_LABELS: Record<RequestFlag, string> = {
  open_queries: 'Open client queries',
  awaiting_client: 'Waiting for the client',
  to_verify: 'Uploads to verify',
  returned: 'Returned for rework',
  overdue: 'Overdue tasks',
  date_proposals: 'Date proposals to review',
};

/**
 * How a product is named everywhere: "Brand name / Generic name", or whichever of the two it has. Requests from before
 * the names were asked keep their product name.
 */
export function displayProductName(r: { productName?: string | null; genericName?: string | null; brandName?: string | null }): string {
  const brand = r.brandName?.trim();
  const generic = r.genericName?.trim();
  if (brand && generic) return brand.toLowerCase() === generic.toLowerCase() ? brand : `${brand} / ${generic}`;
  return brand || generic || r.productName || '';
}
export type RequestOrigin = (typeof REQUEST_ORIGINS)[number];

/** Delivery state of the latest request update to BDS. `off`: the request has no BDS reviewer yet. */
export const BDS_SYNC_STATES = ['off', 'pending', 'synced', 'failed'] as const;
export type BdsSyncState = (typeof BDS_SYNC_STATES)[number];

export const WORK_ITEM_STATUSES = ['assigned', 'submitted', 'returned', 'verified', 'cancelled'] as const;
export type WorkItemStatus = (typeof WORK_ITEM_STATUSES)[number];

export const WORK_ITEM_STATUS_LABELS: Record<WorkItemStatus, string> = {
  assigned: 'Assigned',
  submitted: 'Awaiting verification',
  returned: 'Returned',
  verified: 'Verified',
  cancelled: 'Removed',
};

/** Work items that still count towards a request. */
/** How RA asked for a change to an uploaded version: before verifying it, or by reopening it after. */
export const ITEM_RETURN_KINDS = ['returned', 'reopened'] as const;
export type ItemReturnKind = (typeof ITEM_RETURN_KINDS)[number];

/** What became of one version of the dossier sent to BD. */
export const DOSSIER_ROUND_OUTCOMES = ['pending', 'returned', 'approved'] as const;
export type DossierRoundOutcome = (typeof DOSSIER_ROUND_OUTCOMES)[number];

export const ACTIVE_WORK_ITEM_STATUSES: readonly WorkItemStatus[] = ['assigned', 'submitted', 'returned', 'verified'];

export const PRODUCT_CATEGORIES = ['Herbal', 'Nutraceutical', 'Cosmeceutical'] as const;

export const DOSAGE_FORMS = [
  'Tablet',
  'Capsule',
  'Softgel Capsule',
  'Powder',
  'Granules',
  'Liquid',
  'Ointment',
  'Cream',
  'Paste',
  'Gel',
] as const;

/** Suggestions for the required-documents checklist (from the legacy Document_Types master). */
export const COMMON_DOCUMENTS = [
  'WHO-GMP Certificate',
  'ISO 9001:2015 Certificate',
  'FSSAI License',
  'HALAL Certificate',
  'Free Sale Certificate (Nutra)',
  'Free Sale Certificate (Herbal)',
  'Manufacturing Formula Record (MFR)',
  'Quality and Quantity Document',
  'Raw Material COA',
  'Raw Material STP',
  'Packaging Material COA',
  'Packaging Material STP',
  'Finished Product COA',
  'Finished Product STP',
  'Stability Study Report',
  'Product Artwork / Label',
  'Batch Manufacturing Record (BMR)',
  'Certificate of Pharmaceutical Product (CoPP)',
] as const;

export const WORKFLOW_EVENT_TYPES = [
  'request_created',
  'request_updated',
  'request_cancelled',
  'request_completed',
  'bd_reviewer_assigned',
  'item_added',
  'item_reassigned',
  'item_updated',
  'item_cancelled',
  'target_proposed',
  'target_accepted',
  'target_rejected',
  'item_submitted',
  'item_resubmitted',
  'item_verified',
  'item_returned',
  'item_reopened',
  'bd_sent',
  'bd_resent',
  'bd_approved',
  'bd_returned',
  'client_changes_requested',
  'query_compliance_sent',
  'clarification_requested',
  'clarification_answered',
  'sample_requested',
  'sample_updated',
  'artwork_updated',
  'legacy_event',
] as const;
export type WorkflowEventType = (typeof WORKFLOW_EVENT_TYPES)[number];

export const WORKFLOW_EVENT_LABELS: Record<WorkflowEventType, string> = {
  request_created: 'Request created',
  request_updated: 'Request details updated',
  request_cancelled: 'Request cancelled',
  request_completed: 'Request completed',
  bd_reviewer_assigned: 'SC-NBD reviewer chosen',
  item_added: 'Work item assigned',
  item_reassigned: 'Work item reassigned',
  item_updated: 'Work item updated',
  item_cancelled: 'Work item removed',
  target_proposed: 'Target date proposed',
  target_accepted: 'Target date accepted',
  target_rejected: 'Target date proposal rejected',
  item_submitted: 'Documents submitted',
  item_resubmitted: 'Documents resubmitted',
  item_verified: 'Documents verified',
  item_returned: 'Documents returned',
  item_reopened: 'Verified item reopened',
  bd_sent: 'Sent to BD (BDS)',
  bd_resent: 'Re-sent to BD (BDS)',
  bd_approved: 'Approved by BD',
  bd_returned: 'Returned by BD',
  client_changes_requested: 'Client raised a query',
  query_compliance_sent: 'Compliance sent to the client',
  clarification_requested: 'RA asked the client for more information',
  clarification_answered: 'Client answered RA’s clarification',
  sample_requested: 'Sample requested',
  sample_updated: 'Sample updated',
  artwork_updated: 'Artwork updated',
  legacy_event: 'Legacy event',
};

/** `YYYY-MM-DD` that is also a real calendar date. */
export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** Today's date in the Asia/Kolkata business calendar, as `YYYY-MM-DD`. */
export function businessToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}
